import { MANUAL_SORTING } from '@/lib/constants';
import { propertyType } from 'shared/constants.json';
import { propertyValue as propertyValueConfig } from 'shared/api/query/configs.json';
import { shallowCopy } from 'shared/lib/copy';
import { sortAlphaNumeric, sortByArray, sortByAttr, sortChain, sortDatetime, sortReverseMdlwr } from '@/lib/sort';

export default function useSort(attributeMap, properties) {
  const propertiesMap = properties.value.reduce((res, next) => {
    res[`${next.uid}`] = next;
    return res;
  }, {});

  const getOptionValue = (propertyValues, prop) => {
    const propValue = propertyValues.find((p) => p.property.uid === prop.uid);
    if (propValue === undefined) {
      throw new Error('prop value not found');
    }

    return propValue;
  };

  const sortByProperty = (propertyId) => {
    const prop = propertiesMap[propertyId];
    if (prop === undefined) {
      return () => 0;
    }
    switch (prop.type) {
      case propertyType.space:
      case propertyType.singleSelect:
      case propertyType.options:
      case propertyType.status: {
        const indexMap = prop.options.reduce((acc, next, index) => {
          acc[next.uid] = index;
          return acc;
        }, {});
        return (a, b) => {
          const aPropValue = shallowCopy(getOptionValue(a.properties, prop));
          const aSelectedOptions = aPropValue.selectedOptions.sort(sortByArray(prop.options));
          const bPropValue = shallowCopy(getOptionValue(b.properties, prop));
          const bSelectedOptions = bPropValue.selectedOptions.sort(sortByArray(prop.options));
          for (let i = 0; i < Math.max(aSelectedOptions.length, bSelectedOptions.length); i++) {
            if (aSelectedOptions[i] === undefined && bSelectedOptions[i] === undefined) {
              return 0;
            }
            if (aSelectedOptions[i] === undefined) {
              return -1;
            }
            if (bSelectedOptions[i] === undefined) {
              return 1;
            }
            const aIndex = indexMap[aSelectedOptions[i].uid];
            const bIndex = indexMap[bSelectedOptions[i].uid];
            if (aIndex === bIndex) {
              continue;
            }
            return aIndex - bIndex;
          }

          return 0;
        };
      }
      case propertyType.user: {
        return (a, b) => {
          const aPropValue = shallowCopy(getOptionValue(a.properties, prop));
          const aSelectedUsers = aPropValue.users.sort((a, b) => sortAlphaNumeric(`${a.firstName} ${b.lastName}`, `${b.firstName} ${b.lastName}`));
          const bPropValue = shallowCopy(getOptionValue(b.properties, prop));
          const bSelectedUsers = bPropValue.users.sort((a, b) => sortAlphaNumeric(`${a.firstName} ${b.lastName}`, `${b.firstName} ${b.lastName}`));
          for (let i = 0; i < Math.max(aSelectedUsers.length, bSelectedUsers.length); i++) {
            if (aSelectedUsers[i] === undefined && bSelectedUsers[i] === undefined) {
              return 0;
            }
            if (aSelectedUsers[i] === undefined) {
              return -1;
            }
            if (bSelectedUsers[i] === undefined) {
              return 1;
            }
            const res = sortAlphaNumeric(`${aSelectedUsers[i].firstName} ${aSelectedUsers[i].lastName}`, `${bSelectedUsers[i].firstName} ${bSelectedUsers[i].lastName}`);
            if (res !== 0) {
              return res;
            }
          }

          return 0;
        };
      }
      case propertyType.date: {
        return (a, b) => {
          const aPropValue = shallowCopy(getOptionValue(a.properties, prop));
          const bPropValue = shallowCopy(getOptionValue(b.properties, prop));
          return sortDatetime(aPropValue.timestamp, bPropValue.timestamp);
        };
      }
      case propertyType.text:
      case propertyType.url:
      case propertyType.number:
      default: {
        return (a, b) => {
          const aPropValue = getOptionValue(a.properties, prop);
          const aVal = aPropValue[valueKey[prop.type]];
          const bPropValue = getOptionValue(b.properties, prop);
          const bVal = bPropValue[valueKey[prop.type]];
          return sortAlphaNumeric(aVal, bVal);
        };
      }
    }
  };

  const valueKey = {
    [propertyType.text]: propertyValueConfig.edges.text,
    [propertyType.url]: propertyValueConfig.edges.text,
    [propertyType.number]: propertyValueConfig.edges.number,
  };

  const getAttr = (attr) => {
    if (attributeMap === undefined || attributeMap[attr] === undefined || attributeMap[attr].alias === undefined) {
      return attr;
    }
    return attributeMap[attr].alias;
  };
  const getType = (attr) => {
    if (attributeMap === undefined || attributeMap[attr] === undefined) {
      return undefined;
    }
    return attributeMap[attr].type;
  };
  const getIgnored = (attr) => {
    if (attributeMap === undefined || attributeMap[attr] === undefined || attributeMap[attr].ignored === undefined) {
      return false;
    }
    return attributeMap[attr].ignored;
  };

  const getSorter = (orderItem) => {
    const attr = getAttr(orderItem.attr);

    if (attr === MANUAL_SORTING) {
      return sortByArray(orderItem.sorting);
    }

    switch (getType(orderItem.attr)) {
      case 'cachedProperty':
        return sortByProperty(orderItem.langs[0].substring(1));
      case 'date':
        return sortByAttr(attr, sortDatetime);
      default:
        return sortByAttr(attr, sortAlphaNumeric);
    }
  };

  const sort = (list, order) => {
    const filteredOrder = order.filter((o) => !getIgnored(o.attr));
    if (filteredOrder.length === 0) {
      return list;
    }

    const res = shallowCopy(list);
    res.sort(sortChain(filteredOrder.map((o) => sortReverseMdlwr(o.desc)(getSorter(o)))));

    return res;
  };

  return { sort };
}
